import type { OnDeviceMcpCatalogEntry } from '@/features/integrations/integrations.types';

import type { TestedConnectSpec } from './use-tested-connect';
import type {
  ConnectionTestProblem,
  ConnectionTestResult,
  ServerTransport,
} from '@littlebird/axon-mcp';

export const LOGSEQ_TEMPLATE_ID = 'logseq';

export interface LogseqConnection {
  token: string;
  host: string;
  port: string;
}

/** logseq-mcp falls back to these when its env leaves them unset */
export const LOGSEQ_DEFAULT_HOST = '127.0.0.1';
export const LOGSEQ_DEFAULT_PORT = '12315';

export type LogseqProblem =
  | 'missing_token'
  | 'token_rejected'
  | 'unreachable'
  | ConnectionTestProblem;

export const LOGSEQ_SETUP_STEPS: readonly { title: string; detail: string }[] =
  [
    {
      title: 'Turn on the HTTP API server',
      detail:
        'In Settings, open Features, turn on HTTP API server, then click Restart App.',
    },
    {
      title: 'Create a token',
      detail:
        'Click API at the top of Logseq, choose Authorization tokens, add a token, and click Save.',
    },
    {
      title: 'Start the server',
      detail:
        'Click API again and choose Start server. To keep it running, turn on auto start in Server configurations.',
    },
  ];

const MISSING_TOKEN_RE = /No Logseq API token/;
const TOKEN_REJECTED_RE = /Logseq API error: 40[13]\b/;
// Node's fetch fails with `fetch failed` and the socket error as its cause;
// Bun's fetch names the socket error directly
const UNREACHABLE_RE =
  /ECONNREFUSED|ConnectionRefused|ENOTFOUND|EHOSTUNREACH|fetch failed|Unable to connect/;

/** logseq-mcp checks the Logseq API before it answers MCP, so its stderr says why a test failed */
export function classifyLogseqFailure(
  result: ConnectionTestResult
): LogseqProblem {
  const problem = result.problem ?? 'connection_failed';
  if (problem !== 'connection_failed') return problem;

  const log = result.error ?? '';
  if (MISSING_TOKEN_RE.test(log)) return 'missing_token';
  if (TOKEN_REJECTED_RE.test(log)) return 'token_rejected';
  if (UNREACHABLE_RE.test(log)) return 'unreachable';
  return 'connection_failed';
}

export function describeLogseqProblem(
  problem: LogseqProblem,
  connection: Pick<LogseqConnection, 'host' | 'port'>,
  { inSetup }: { inSetup: boolean }
): string {
  switch (problem) {
    case 'unreachable':
      return `Logseq didn’t answer at ${connection.host}:${connection.port}. Check that Logseq is open and the API server is on${inSetup ? ' (step 3)' : ''}.`;
    case 'token_rejected':
      return 'Logseq rejected the token. Copy it again from API, then Authorization tokens, in Logseq.';
    case 'missing_token':
      return 'Paste your Logseq API token to connect.';
    case 'timed_out':
      return 'Logseq is taking longer than expected to connect. Try again.';
    case 'certificate_untrusted':
      return 'Littlebird couldn’t download Logseq. Your network inspects secure connections with a certificate Littlebird doesn’t trust. Ask your IT team for help.';
    case 'connection_failed':
      return 'Logseq couldn’t connect. Try again.';
  }
}

export function logseqEnv(
  connection: LogseqConnection
): Record<string, string> {
  return {
    LOGSEQ_TOKEN: connection.token.trim(),
    LOGSEQ_HOST: connection.host.trim(),
    LOGSEQ_PORT: connection.port.trim(),
  };
}

export function logseqConnectionFromEnv(
  env: Record<string, string> | undefined
): LogseqConnection {
  return {
    token: env?.LOGSEQ_TOKEN ?? '',
    host: env?.LOGSEQ_HOST || LOGSEQ_DEFAULT_HOST,
    port: env?.LOGSEQ_PORT || LOGSEQ_DEFAULT_PORT,
  };
}

export function buildLogseqTransport(
  entry: OnDeviceMcpCatalogEntry,
  connection: LogseqConnection
): ServerTransport {
  return {
    kind: 'stdio',
    runtime: entry.runtime,
    packageSpec: entry.package_spec,
    env: logseqEnv(connection),
  };
}

export const LOGSEQ_CONNECT: TestedConnectSpec<
  LogseqConnection,
  LogseqProblem
> = {
  templateId: LOGSEQ_TEMPLATE_ID,
  initialConnection: {
    token: '',
    host: LOGSEQ_DEFAULT_HOST,
    port: LOGSEQ_DEFAULT_PORT,
  },
  isComplete: (connection) => connection.token.trim() !== '',
  buildTransport: buildLogseqTransport,
  classifyFailure: classifyLogseqFailure,
};
